// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Redirect } from 'react-router-dom';

// bernie
import { ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { observer } from 'mobx-react';
import { PzCampaignStore, PzStore, UserAuthStore, UserStore } from 'stores';

// util
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';

// ui
import { UitkEmailInput, UitkPasswordInput, UitkTextInput } from 'uitk-react-input';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkText, UitkHeading } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLink } from 'uitk-react-link';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkTertiaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkSelect } from 'uitk-react-select';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';

// components
import { PzFormInfoDialog } from 'components/signup/pz-form-info-dialog';
import { PzTermsDialog } from 'components/signup/pz-terms-dialog';
import { Program, PzCampaign } from 'models';

interface SignUpFormPZProps {
  context: ContextStore;
  l10n?: ILocalization;
  routeStore: RouteStore;
  user?: UserStore;
  pzStore?: PzStore;
  program?: Program;
  userAuth?: UserAuthStore;
  terms?: string;
  termsId?: string;
  termsState?: string;
  campaignTerms?: { terms: string; terms_id: string };
}

export const PzSignUpForm: React.FunctionComponent<SignUpFormPZProps> = ({
  context,
  l10n,
  routeStore,
  user,
  pzStore,
  terms,
  termsId,
  termsState,
  campaignTerms,
  program,
  userAuth,
}) => {
  const locale = modifyLocaleParam(context.locale);

  const [formError, setFormError] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [email, setEmail] = React.useState(null);
  const [camRef, setCamRef] = React.useState(null);
  const [partnerID, setPartnerID] = React.useState(null);
  const [checkboxValue, setCheckboxValue] = React.useState({ pzTc: false, vrboTc: false, ahTc: false });
  const [infoEntry, setInfoEntry] = React.useState('');
  const { control, errors, handleSubmit, watch } = useForm({ mode: 'onBlur' });

  // labels
  const emailLabel = l10n.formatText('email');
  const countryLabel = l10n.formatText('sign.up.country');
  const usernameLabel = l10n.formatText('username');
  const passwordLabel = l10n.formatText('password');
  const pzSignupUsername = 'Partnerize ' + l10n.formatText('username');
  const pzSignupAccountName = 'Partnerize ' + l10n.formatText('pz.signup.form.fields.labels.account_name');
  const pzSignupPassword = 'Partnerize ' + l10n.formatText('password');
  const pzSignupSubmitLabel = l10n.formatText('sign.up');
  const pzSignupAddress = l10n.formatText('sign.up.address');
  const pzSignupPostcode = l10n.formatText('sign.up.postcode');
  const pzSignupPhone = l10n.formatText('sign.up.phone');
  const confirmPasswordLabel = l10n.formatText('confirm.password');
  const pzTermsAndConditionsLabel = l10n.formatText('pz.signup.form.label.pz_tc');
  const vrboTermsAndConditionsLabel = l10n.formatText('pz.signup.form.label.program_tc', program.title);

  // missing labels
  const emailMissingErrorLabel = l10n.formatText('sign.up.email.missing.label');
  const passwordMissingErrorLabel = l10n.formatText('sign.in.email.missing.password'); // Should be sign.up
  const countryMissingErrorLabel = l10n.formatText('sign.up.country.missing.label');
  const usernameMissingErrorLabel = l10n.formatText('sign.up.username.missing.label');
  const accountNameMissingErrorLabel = l10n.formatText(
    'pz.signup.form.fields.account_name.rules.please_enter_account_name',
  );
  const addressMissingErrorLabel = l10n.formatText('sign.up.address.missing.label');
  const postcodeMissingErrorLabel = l10n.formatText('sign.up.postcode.missing.label');
  const phoneMissingErrorlabel = l10n.formatText('sign.up.phone.missing.label');

  // invalid labels
  const emailInvalidErrorLabel = l10n.formatText('sign.up.email.invalid.label');
  const confirmPasswordMissingErrorLabel = l10n.formatText('set.password.confirm.missing.label');
  const pzTermsAndConditionsMissingErrorLabel = l10n.formatText('pz.signup.form.terms_and_conditions.missing.label');
  const pzTermsofUseMissingErrorLabel = l10n.formatText('pz.signup.form.terms_of_use.missing.label');
  const confirmPasswordMismatchLabel = l10n.formatText('set.password.confirm.mismatch.label');
  const pzPasswordRulesUppercase = l10n.formatText('pz.password.rules.item1');
  const pzPasswordRulesLowercase = l10n.formatText('pz.password.rules.item2');
  const pzPasswordRulesValidLength = l10n.formatText('pz.password.rules.item3');
  const pzPasswordRulesHasSpecialChar = l10n.formatText('pz.password.rules.item4');
  const pzPasswordRulesHasNumber = l10n.formatText('pz.password.rules.item5');
  const pzUserRulesAlphanumeric = l10n.formatText('pz.username.rules.item1');
  const pzUserRulesMustUnique = l10n.formatText('pz.username.rules.item2');
  const pzUserRulesLengthMin = l10n.formatText('pz.username.rules.item3');
  const pzUserRulesLengthMax = l10n.formatText('pz.username.rules.item4');
  const pzAccountNameRulesAlphanumeric = l10n.formatText(
    'pz.signup.form.fields.account_name.rules.account_name_can_only_contain_alphanumeric_chars',
  );
  const pzAccountNameRulesLengthMin = l10n.formatText(
    'pz.signup.form.fields.account_name.rules.account_name_length_must_be_greater_than_2',
  );
  const pzAccountNameRulesLengthMax = l10n.formatText(
    'pz.signup.form.fields.account_name.rules.account_name_length_must_not_be_greater_than_32',
  );
  const pzAccountNameRulesContainsUsername = l10n.formatText(
    'pz.signup.form.fields.account_name.rules.account_name_cannot_contain_username',
  );

  // error labels
  const userAccountExistsErrorLabel = l10n.formatText('sign.up.existing.email.error.message');
  const unknownServerErrorLabel = l10n.formatText('verify.program.status.unknown.server.error');

  // tslint:disable-next-line:no-require-imports
  const { countries } = require('../../models/pz-country.json');
  const sortedCountryList = countries
    .map((item) => {
      return [item.country.printable_name, item.country.iso];
    })
    .sort();

  const scrollWindow = (xpos, ypos) => {
    window.scrollTo(xpos, ypos);
  };

  const onSubmit = handleSubmit((fields) => {
    if (fields.accountName.includes(fields.username)) {
      setFormError(l10n.formatText('pz.signup.form.fields.account_name.rules.account_name_cannot_contain_username'));

      return;
    }
    if (fields.username.includes(fields.accountName)) {
      setFormError(l10n.formatText('pz.signup.form.fields.username.rules.username_cannot_contain_account_name'));

      return;
    }

    fields.pzTermsId = termsId;
    fields.campaignTermsId = campaignTerms.terms_id;

    setStatus('LOADING');
    setEmail(fields.email);
    setFormError(null);

    triggerSignup(fields);
  });

  const triggerSignup = async (fields) => {
    await pzStore.createUser(program.name, fields).then((response) => {
      setStatus(null);
      setFormError(null);
      setCamRef(null);
      setPartnerID(null);

      if (response.success) {
        const partnerId = response.body.partnerId;
        const camref = response.body.camref;
        const affiliatePartnershipStatus = 'APPROVED';
        const accountName = fields.accountName;
        userAuth
          .updateUserProgramRelationship(
            program.brand,
            program.posa,
            program.network,
            partnerId,
            false,
            camref,
            affiliatePartnershipStatus,
            accountName,
          )
          .then((pgmRelationResponse) => {
            if (pgmRelationResponse.status === 201) {
              setCamRef(camref);
              setPartnerID(partnerId);
            } else {
              setFormError(unknownServerErrorLabel);
            }
          });
      } else {
        if (response.body) {
          if (response.body.message === 'USER_ACCOUNT_EXISTS_ERROR') {
            setFormError(userAccountExistsErrorLabel);
          } else {
            setFormError(response.error);
          }
        } else {
          setFormError(response.error);
        }
        scrollWindow(0, 0);
      }
    });
  };

  const registerOptions = {
    username: {
      required: usernameMissingErrorLabel,
      minLength: {
        value: 2,
        message: pzUserRulesLengthMin,
      },
      maxLength: {
        value: 32,
        message: pzUserRulesLengthMax,
      },
      pattern: {
        value: /^\w[0-9a-zA-Z]+$/,
        message: pzUserRulesAlphanumeric,
      },
    },
    accountName: {
      required: accountNameMissingErrorLabel,
      minLength: {
        value: 2,
        message: pzAccountNameRulesLengthMin,
      },
      maxLength: {
        value: 32,
        message: pzAccountNameRulesLengthMax,
      },
      pattern: {
        value: /^\w[0-9a-zA-Z]+$/,
        message: pzAccountNameRulesAlphanumeric,
      },
      validate: {
        userNameInAccountNameRule: (value) =>
          watch('username') === '' || !value.includes(watch('username')) || pzAccountNameRulesContainsUsername,
      },
    },
    password: {
      required: passwordMissingErrorLabel,
      minLength: {
        value: 8,
        message: pzPasswordRulesValidLength,
      },
      validate: {
        upperCase: (value) => value.toLowerCase() !== value || pzPasswordRulesUppercase,
        lowerCase: (value) => value.toUpperCase() !== value || pzPasswordRulesLowercase,
        hasNumber: (value) => /\d/.test(value) || pzPasswordRulesHasNumber,
        hasSpecialChar: (value) => /[`!@#$%^&*()_+\-=\]{};':"\\|,.<>?~]/.test(value) || pzPasswordRulesHasSpecialChar,
      },
    },
    confirmPassword: {
      required: confirmPasswordMissingErrorLabel,
      validate: (value) => value === watch('password') || confirmPasswordMismatchLabel,
    },
    email: {
      required: emailMissingErrorLabel,
      pattern: {
        value: /^[a-zA-Z0-9_\.\-\+](?:[a-z0-9!#$%&'*+/=?^_`{|}~-]*(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i,
        message: emailInvalidErrorLabel,
      },
    },
    address: {
      required: addressMissingErrorLabel,
    },
    postcode: {
      required: postcodeMissingErrorLabel,
    },
    phone: {
      required: phoneMissingErrorlabel,
    },
    country: {
      required: countryMissingErrorLabel,
    },
    termsandcondition: {
      required: pzTermsAndConditionsMissingErrorLabel,
    },
    termsofuse: {
      required: pzTermsofUseMissingErrorLabel,
    },
  };

  const handleInfo = (entry) => {
    setInfoEntry(entry);
  };

  if (termsState === 'error') {
    return (
      <UitkSpacing margin={{ blockstart: 'three' }}>
        <div className="PzSignup__error">
          <UitkSpacingHr />
          <UitkEmptyState>
            <UitkIcon name="sentiment_2" size={UitkIconSize.LARGE} />
            <UitkHeading size={5} tag="h4">
              We're having trouble loading the form
            </UitkHeading>
            <UitkText size={300}>Click below to reload the page or try again later.</UitkText>
            <UitkEmptyStateActionArea>
              <UitkTertiaryButton
                name="textonly"
                text="Reload"
                type="button"
                onClick={() => window.location.reload()}
              />
            </UitkEmptyStateActionArea>
          </UitkEmptyState>
        </div>
      </UitkSpacing>
    );
  }

  const hasNoTerms = !terms || (terms && !terms.length);
  const hasNoCampaignTerms = !campaignTerms || (campaignTerms && !campaignTerms.terms);

  return (
    <>
      <form name={'pzSignUpForm'} onSubmit={onSubmit} method="POST" className="PzSignup__form">
        {formError && (
          <UitkSpacing margin={{ blockend: 'six' }}>
            <div>
              <UitkErrorSummary heading={formError} />
            </div>
          </UitkSpacing>
        )}

        {camRef && partnerID && (
          <Redirect
            to={{
              pathname: `/${locale}/pz-signup-confirmation`,
              state: {
                camRef: `${camRef}`,
                partnerId: `${partnerID}`,
                program: {
                  name: program.name,
                  title: program.title,
                },
              },
            }}
          />
        )}

        <UitkLayoutFlex direction={'column'} space="three">
          <UitkLayoutFlexItem>
            <div>
              <UitkHeading tag="h4" size={5}>
                <LocalizedText message="pz.signup.create.account" />
              </UitkHeading>
              <UitkText inline={true} size={300}>
                <LocalizedText message="pz.signup.create.account.description" />{' '}
                <UitkLink inline={true} onClick={() => handleInfo('eligibility')}>
                  <Link
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: 'pzFormInfo',
                      }),
                    }}
                    role="button"
                  >
                    <LocalizedText message="learn.more" />
                  </Link>
                </UitkLink>
              </UitkText>
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <div>
              <UitkSpacing margin={{ block: 'two' }}>
                <UitkMoreInfoTrigger onClick={() => handleInfo('username')}>
                  <Link
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: 'pzFormInfo',
                      }),
                    }}
                    role="button"
                  >
                    <UitkText size={300}>Username</UitkText>
                    <UitkIcon name="info_outline" />
                  </Link>
                </UitkMoreInfoTrigger>
              </UitkSpacing>
              <Controller
                control={control}
                name="username"
                rules={registerOptions.username}
                render={({ onChange, onBlur, value, name }) => (
                  <UitkTextInput
                    id="signUpFormUsernameInput"
                    name={name}
                    label={pzSignupUsername}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    data-cy="sign-up-username-field"
                    invalid={errors.username?.message}
                  />
                )}
              />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <div>
              <UitkSpacing margin={{ block: 'two' }}>
                <UitkMoreInfoTrigger onClick={() => handleInfo('password')}>
                  <Link
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: 'pzFormInfo',
                      }),
                    }}
                    role="button"
                  >
                    <UitkText size={300}>Password</UitkText>
                    <UitkIcon name="info_outline" />
                  </Link>
                </UitkMoreInfoTrigger>
              </UitkSpacing>
              <Controller
                control={control}
                name="password"
                rules={registerOptions.password}
                render={({ onChange, onBlur, value, name }) => (
                  <UitkPasswordInput
                    id="passwordInput"
                    label={pzSignupPassword}
                    name={name}
                    value={value}
                    onChange={({ target: { value: passwordValue } }) => {
                      onChange(passwordValue);
                    }}
                    onBlur={onBlur}
                    invalid={errors.password?.message}
                    data-cy="sign-up-password-field"
                  />
                )}
              />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="confirmPassword"
              rules={registerOptions.confirmPassword}
              render={({ onChange, onBlur, value, name }) => (
                <UitkPasswordInput
                  id="confirmPasswordInput"
                  label={confirmPasswordLabel}
                  name={name}
                  value={value}
                  onChange={({ target: { value: confirmPasswordValue } }) => {
                    onChange(confirmPasswordValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.confirmPassword?.message}
                  data-cy="sign-up-password-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <div>
              <UitkSpacing margin={{ block: 'two' }}>
                <UitkMoreInfoTrigger onClick={() => handleInfo('accountName')}>
                  <Link
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: 'pzFormInfo',
                      }),
                    }}
                    role="button"
                  >
                    <UitkText size={300}>{l10n.formatText('pz.signup.form.fields.labels.account_name')}</UitkText>
                    <UitkIcon name="info_outline" />
                  </Link>
                </UitkMoreInfoTrigger>
              </UitkSpacing>
              <Controller
                control={control}
                name="accountName"
                rules={registerOptions.accountName}
                render={({ onChange, onBlur, value, name }) => (
                  <UitkTextInput
                    id="signUpFormAccountNameInput"
                    name={name}
                    label={pzSignupAccountName}
                    value={value}
                    onChange={({ target: { value: newValue } }) => {
                      onChange(newValue);
                    }}
                    onBlur={onBlur}
                    data-cy="sign-up-accountname-field"
                    invalid={errors.accountName?.message}
                  />
                )}
              />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="email"
              rules={registerOptions.email}
              render={({ onChange, onBlur, value, name }) => (
                <UitkEmailInput
                  id="signUpFormEmailInput"
                  name={name}
                  label={emailLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.email?.message}
                  data-cy="sign-up-email-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="address1"
              rules={registerOptions.address}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormAddressInput"
                  name={name}
                  label={pzSignupAddress}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.address1?.message}
                  data-cy="sign-up-address-1-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="postcode"
              rules={registerOptions.postcode}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormPostcodeInput"
                  name={name}
                  label={pzSignupPostcode}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.postcode?.message}
                  data-cy="sign-up-postcode-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="phone"
              rules={registerOptions.phone}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormPhoneInput"
                  name={name}
                  label={pzSignupPhone}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.phone?.message}
                  data-cy="sign-up-phone-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="country"
              rules={registerOptions.country}
              render={({ onChange, onBlur, value, name }) => (
                <UitkSelect
                  id="signUpFormCountryInput"
                  name={name}
                  label={countryLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    pzStore.fetchTerms(newValue);
                    setCheckboxValue({ ...checkboxValue, pzTc: false });
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  emptyState={true}
                  invalid={errors.country?.message}
                  data-cy="sign-up-country-field"
                >
                  {sortedCountryList.map((item, i) => {
                    return (
                      <option key={i} value={item[1]}>
                        {item[0]}
                      </option>
                    );
                  })}
                </UitkSelect>
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkCheckboxGroup group="checkboxGroup">
              <Controller
                control={control}
                name="pzSignupPzTc"
                defaultValue={checkboxValue.pzTc}
                rules={registerOptions.termsandcondition}
                render={({ onChange, value, name }) => (
                  <div className={`PzCheckbox ${termsState === 'pending' ? 'PzCheckbox--pending' : ''}`}>
                    <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
                    <UitkCheckbox
                      id="pzSignupPzTc"
                      name={name}
                      label={pzTermsAndConditionsLabel}
                      checked={checkboxValue.pzTc}
                      disabled={hasNoTerms}
                      onChange={({ target: { checked } }) => {
                        onChange(checked);
                        setCheckboxValue({ ...checkboxValue, pzTc: checked });
                      }}
                      errorMessage={errors.pzSignupPzTc?.message}
                    >
                      <LocalizedText message="pz.signup.form.tc.text.read_accept" />{' '}
                      <UitkLink inline={true}>
                        <Link
                          to={{
                            pathname: routeStore.pathname,
                            search: routeStore.mergeQueryString(routeStore.search, {
                              pwaDialog: 'pzTerms',
                            }),
                          }}
                          role="button"
                        >
                          <LocalizedText message="pz.signup.form.label.pz_tc" />
                        </Link>
                      </UitkLink>
                    </UitkCheckbox>
                  </div>
                )}
              />
              <Controller
                control={control}
                name="pzSignupVrboTc"
                defaultValue={false}
                rules={registerOptions.termsandcondition}
                render={({ onChange, value, name }) => (
                  <UitkCheckbox
                    id="pzSignupVrboTc"
                    name={name}
                    label={vrboTermsAndConditionsLabel}
                    checked={value}
                    disabled={hasNoCampaignTerms}
                    onChange={({ target: { checked } }) => {
                      onChange(checked);
                      setCheckboxValue({ ...checkboxValue, vrboTc: checked });
                    }}
                    errorMessage={errors.pzSignupVrboTc?.message}
                  >
                    <LocalizedText message="pz.signup.form.tc.text.read_accept" />{' '}
                    <UitkLink inline={true}>
                      <Link
                        to={{
                          pathname: `/${locale}/entity-terms-and-conditions`,
                          search: `?programName=${program.name}`,
                        }}
                        target={'_blank'}
                      >
                        <LocalizedText message="pz.signup.form.label.program_tc" data={[program.title]} />
                      </Link>
                    </UitkLink>
                  </UitkCheckbox>
                )}
              />
              <Controller
                control={control}
                name="pzSignupAhTc"
                defaultValue={false}
                rules={registerOptions.termsofuse}
                render={({ onChange, value, name }) => (
                  <UitkCheckbox
                    id="pzSignupAhTc"
                    name={name}
                    label="Affiliate Hub Terms of Use and Privacy Policy"
                    checked={value}
                    onChange={({ target: { checked } }) => {
                      onChange(checked);
                      setCheckboxValue({ ...checkboxValue, ahTc: checked });
                    }}
                    errorMessage={errors.pzSignupAhTc?.message}
                  >
                    <LocalizedText message="pz.signup.form.tc.text.read_accept" /> Affiliate Hub{' '}
                    <UitkLink inline={true}>
                      <Link to={`/${locale}/terms-of-use`} target="_blank">
                        <LocalizedText message="sign.up.termsofuse" />
                      </Link>
                    </UitkLink>{' '}
                    <LocalizedText message="pz.signup.form.tc.text.and" />{' '}
                    <UitkLink inline={true}>
                      <Link to={`/${locale}/privacy-policy`} target="_blank">
                        <LocalizedText message="sign.up.privacy.policy" />
                      </Link>
                    </UitkLink>
                  </UitkCheckbox>
                )}
              />
            </UitkCheckboxGroup>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkPrimaryButton
              data-cy="sign-up-submit"
              id="signUpFormSubmitButton"
              text={status === 'LOADING' ? '' : pzSignupSubmitLabel}
              type="submit"
              className={'spacing-top-bottom-24'}
              disabled={errors.length > 0 || status === 'LOADING'}
              size={UitkButtonSize.LARGE}
            >
              {status === 'LOADING' ? '' : 'Sign up'}
              {status === 'LOADING' && <UitkLoader isSmall={true} isDarkBackground={true} />}
            </UitkPrimaryButton>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </form>
      <PzFormInfoDialog dialogId="pzFormInfo" infoEntry={infoEntry} l10n={l10n} routeStore={routeStore} />
      <PzTermsDialog dialogId="pzTerms" l10n={l10n} routeStore={routeStore} terms={terms} />
    </>
  );
};
